import { type VariantProps, cva } from "class-variance-authority";
import { useMemo } from "react";

import { StatusCount } from "@/components/StatusCount";
import { Text } from "@/components/Typography/Text";

import { HStack, VStack } from "../Layout";

const progressVariants = cva("", {
  variants: {
    segment: {
      default: "w-[7px] h-[14px] mx-1",
      small: "w-[6px] h-[12px] mx-[2px]",
    },
  },
  defaultVariants: {
    segment: "default",
  },
});

export interface ProgressProps extends VariantProps<typeof progressVariants> {
  label?: string;
  completed: number;
  total: number;
  segmentLength?: number;
  size?: "default" | "small";
  "data-testid"?: string;
}

export function Progress({
  label,
  completed,
  total,
  segmentLength = 10,
  size = "default",
  "data-testid": testId,
}: ProgressProps) {
  const clampedCompleted = Math.min(Math.max(completed, 0), total);
  const clampedTotal = Math.max(total, 0);
  const segmentCount = Math.min(clampedTotal, segmentLength);
  const tasksPerSegment = clampedTotal / segmentCount;
  const completedSegments = Math.floor(clampedCompleted / tasksPerSegment);

  const segments = useMemo(
    () =>
      Array.from({ length: segmentCount }, (_, index) => (
        <div
          key={index}
          className={`${progressVariants({ segment: size })} ${
            index < completedSegments
              ? "bg-success-on-neutral-fill"
              : "bg-gray-300"
          }`}
        />
      )),
    [segmentCount, completedSegments, size],
  );

  return (
    <VStack className="min-w-fit" role="progressbar">
      {label && (
        <Text
          className="text-neutral-on-muted-fill"
          variant={size === "default" ? "label" : "labelSmallReg"}
          id={`${testId}-label`}
        >
          {label}:
        </Text>
      )}
      <HStack className="items-baseline justify-between gap-1">
        <HStack>{segments}</HStack>
        <StatusCount
          current={clampedCompleted}
          total={clampedTotal}
          variant="success"
          size={size}
        />
      </HStack>
    </VStack>
  );
}
